// Recalcula el overflow del menú (overflow-list) una vez que las fuentes web
// terminan de cargar, para que el header no quede mostrando "Más" cuando en
// realidad los ítems ya caben. Horizon mide el overflow con las métricas de
// la fuente de respaldo durante el render inicial y no vuelve a medir cuando
// la fuente real llega (ResizeNotifier descarta su primera notificación).
// El evento 'reflow' es la vía pública del propio componente para forzar ese
// recálculo (assets/overflow-list.js:110-115).
//
// Contexto (commit dee8a9d, revertido en 1ff09ad):
// Un primer intento disparaba 'reflow' apenas resolvía document.fonts.ready,
// sin ninguna de las guardas de abajo. Eso rompía el header en dos formas:
//   - El custom element podía no estar definido/conectado todavía cuando el
//     script corría, así que dispatchEvent no llegaba a ningún listener útil.
//   - Si el header ya estaba en data-menu-style="drawer" (pantallas chicas,
//     o el propio primer render antes de que se decidiera el modo), la regla
//     `#header-component[...] :is(.header-menu, ...) { display: none }`
//     (sections/header.liquid:500) deja .header-menu en display:none. Medir
//     ahí hace que getBoundingClientRect() devuelva 0 para todo, por lo que
//     overflow-list considera que TODOS los ítems desbordan y fija
//     minimum-reached, lo que a su vez traba setHeaderMenuStyle() en 'drawer'
//     para siempre (assets/utilities.js:786-795).
//
// Este script solo dispara 'reflow' cuando el árbol es realmente medible:
// componente definido y conectado, header en modo "menu" y .header-menu
// visible. Revalida esas condiciones otra vez después de esperar a las
// fuentes, porque ese await puede tardar y el estado del header pudo cambiar
// mientras tanto (resize, cambio a drawer, navegación del theme editor).
(async () => {
  const controller = new AbortController();
  const { signal } = controller;

  const header = document.querySelector('.header-section, #header-component')?.closest('.shopify-section');

  // El theme editor puede reemplazar esta sección sin recargar la página.
  // Si eso pasa mientras seguimos esperando, abortamos: el elemento que
  // encontremos después de eso ya no sería el actual.
  header?.addEventListener('shopify:section:unload', () => controller.abort(), { signal, once: true });

  // Guarda 1: no tocar nada hasta que el custom element exista como clase.
  await customElements.whenDefined('overflow-list');
  if (signal.aborted) return;

  const overflowList = document.querySelector('[data-testid="header-menu-overflow-list"]');

  // Guarda 1b: además de definido, tiene que existir y estar conectado.
  // Con .header-menu en display:none o el elemento ausente del DOM, no hay
  // nada que medir todavía.
  if (!(overflowList instanceof HTMLElement) || !overflowList.isConnected) {
    controller.abort();
    return;
  }

  const headerComponent = document.querySelector('#header-component');

  if (!(headerComponent instanceof HTMLElement)) {
    controller.abort();
    return;
  }

  /**
   * @returns {boolean} true si el header está en modo "menu" y .header-menu
   * es visible, es decir, si overflow-list tiene algo real que medir.
   */
  const isMenuMeasurable = () => {
    const headerMenu = headerComponent.querySelector('.header-menu');
    return (
      headerComponent.dataset.menuStyle === 'menu' &&
      headerMenu instanceof HTMLElement &&
      getComputedStyle(headerMenu).display !== 'none'
    );
  };

  // Guarda 2: en modo "drawer" .header-menu está en display:none. Disparar
  // 'reflow' contra un árbol invisible fue justo la causa de la regresión
  // revertida en 1ff09ad, así que directamente no hacemos nada.
  if (!isMenuMeasurable()) {
    controller.abort();
    return;
  }

  const fontsReady = document.fonts?.ready;
  if (!fontsReady) {
    controller.abort();
    return;
  }

  // Guarda 3: solo esperamos a las fuentes una vez que ya sabemos que hay
  // algo medible; evita colgar una promesa a mitad de un dispatch inútil.
  await fontsReady;
  if (signal.aborted) return;

  // El estado pudo cambiar mientras esperábamos (resize, cambio a drawer,
  // desconexión del elemento): revalidamos antes de disparar el evento.
  if (!overflowList.isConnected || !isMenuMeasurable()) {
    controller.abort();
    return;
  }

  overflowList.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
  controller.abort();
})();
